/**
 * RN-8513 (follows RN-8431): react-native-web's useElementLayout observes in
 * useLayoutEffect. Chrome can deliver the initial ResizeObserver notifications
 * in that same frame, and UIManager.measure then reads layout
 * (getBoundingClientRect / offset*) during the first React 19 commit. On
 * desktop RNVJ that shows up as a long scheduler task with forced layout / TBT.
 *
 * Same contract as react-native-web 0.21's hook, except:
 * - observe() is deferred until idle (rAF used to still run in the TBT window)
 * - onLayout is skipped when the measured box did not change, so duplicate
 *   sizes do not schedule another React flush
 *
 * Copyright (c) Nicolas Gallagher. MIT (react-native-web useElementLayout).
 */

import { useEffect, useLayoutEffect as useReactLayoutEffect } from "react";
// Deep import so this module does not cycle through react-native-web's barrel
// (View → useElementLayout → this file).
// @ts-expect-error react-native-web ships no types for this internal module
import UIManager from "react-native-web/dist/exports/UIManager";

const canUseDOM = typeof window !== "undefined" && window.document != null;
const useLayoutEffect = canUseDOM ? useReactLayoutEffect : useEffect;

const DOM_LAYOUT_HANDLER_NAME = "__reactLayoutHandler";

type LayoutBox = {
  x: number;
  y: number;
  width: number;
  height: number;
  left: number;
  top: number;
};

type LayoutEvent = {
  nativeEvent: {
    layout: LayoutBox;
  };
  timeStamp: number;
};

type OnLayout = (event: LayoutEvent) => void;

type LayoutNode = Element & {
  [DOM_LAYOUT_HANDLER_NAME]?: OnLayout | null;
};

type MeasureFn = (
  node: Element,
  callback: (
    x: number,
    y: number,
    width: number,
    height: number,
    left: number,
    top: number,
  ) => void,
) => void;

const measure: MeasureFn = (UIManager as { measure: MeasureFn }).measure;

const lastLayoutByNode = new WeakMap<Element, LayoutBox>();
// Kept separate from lastLayoutByNode: UIManager.measure reports the rounded
// border box (offsetWidth/offsetHeight) while entry.contentRect is the
// fractional content box, so the two are not comparable.
const lastContentSizeByNode = new WeakMap<Element, { width: number; height: number }>();

function isSameLayout(a: LayoutBox, b: LayoutBox): boolean {
  return (
    a.x === b.x &&
    a.y === b.y &&
    a.width === b.width &&
    a.height === b.height &&
    a.left === b.left &&
    a.top === b.top
  );
}

// observe() produces the initial notification, i.e. the first onLayout, so it
// must not wait on idle time that may never come.
const OBSERVE_IDLE_TIMEOUT_MS = 100;

function scheduleDeferredObserve(run: () => void): () => void {
  if (typeof requestIdleCallback === "function") {
    const id = requestIdleCallback(
      () => {
        run();
      },
      { timeout: OBSERVE_IDLE_TIMEOUT_MS },
    );
    return () => cancelIdleCallback(id);
  }
  const id = setTimeout(run, 0);
  return () => clearTimeout(id);
}

let resizeObserver: ResizeObserver | null = null;
let didWarn = !canUseDOM;

function getResizeObserver(): ResizeObserver | null {
  if (canUseDOM && typeof window.ResizeObserver !== "undefined") {
    if (resizeObserver == null) {
      resizeObserver = new window.ResizeObserver((entries) => {
        entries.forEach((entry) => {
          const node = entry.target as LayoutNode;
          const onLayout = node[DOM_LAYOUT_HANDLER_NAME];
          if (typeof onLayout !== "function") {
            return;
          }

          const contentRect = entry.contentRect;
          const prevContentSize = lastContentSizeByNode.get(node);
          if (
            prevContentSize != null &&
            prevContentSize.width === contentRect.width &&
            prevContentSize.height === contentRect.height
          ) {
            return;
          }
          lastContentSizeByNode.set(node, {
            width: contentRect.width,
            height: contentRect.height,
          });

          measure(node, (x, y, width, height, left, top) => {
            const layout = { x, y, width, height, left, top };
            const last = lastLayoutByNode.get(node);
            if (last != null && isSameLayout(last, layout)) {
              return;
            }
            lastLayoutByNode.set(node, layout);
            const event = {
              nativeEvent: {
                layout,
              },
              timeStamp: Date.now(),
            };
            Object.defineProperty(event.nativeEvent, "target", {
              enumerable: true,
              get: () => entry.target,
            });
            onLayout(event);
          });
        });
      });
    }
  } else if (!didWarn) {
    if (process.env.NODE_ENV !== "production" && process.env.NODE_ENV !== "test") {
      console.warn(
        "onLayout relies on ResizeObserver which is not supported by your browser. " +
          "Please include a polyfill, e.g., https://github.com/que-etc/resize-observer-polyfill.",
      );
      didWarn = true;
    }
  }
  return resizeObserver;
}

export default function useElementLayout(
  ref: { current: LayoutNode | null },
  onLayout?: OnLayout | null,
): void {
  const observer = getResizeObserver();

  useLayoutEffect(() => {
    const node = ref.current;
    if (node != null) {
      node[DOM_LAYOUT_HANDLER_NAME] = onLayout;
    }
  }, [ref, onLayout]);

  // Observing is a separate effect so this does not re-run when onLayout changes.
  useLayoutEffect(() => {
    const node = ref.current;
    if (node == null || observer == null) {
      return;
    }

    let cancelDeferred: (() => void) | undefined;
    if (typeof node[DOM_LAYOUT_HANDLER_NAME] === "function") {
      cancelDeferred = scheduleDeferredObserve(() => {
        observer.observe(node);
      });
    } else {
      observer.unobserve(node);
    }

    return () => {
      cancelDeferred?.();
      observer.unobserve(node);
      // observe() only reports an initial layout for sizes it has not already
      // deduped, so a node that is observed again must start from a clean slate.
      lastContentSizeByNode.delete(node);
      lastLayoutByNode.delete(node);
    };
  }, [ref, observer]);
}
